import {useHandleNavigationWithSpecialKeys, useHistory} from '@aboutyou/router';
import React, {ButtonHTMLAttributes} from 'react';
import styled from 'styled-components';
import {COLORS} from '../../../constants/colors';
import {Icon} from '../../atoms/Icon/Icon';
import {GenericIconName, IconName} from '../../atoms/Icon/IconName';
import {
  BadgeList,
  ChildrenWrapper,
  StyledLink,
  StyledLinkButton,
  StyledText,
} from './styled';

interface NavigationTreeProps {
  nodes: TreeNodeProps[];
  trackOrigin: VoidFunction;
  testId?: string;
  useButtonLink?: boolean;
  allDealItems?: React.ReactNode;
}

export interface TreeNodeProps {
  id?: number;
  label: string;
  path: string;
  textColor?: string;

  disabled: boolean;
  active: boolean;
  current: boolean;

  badges?: Badge[];
  children?: TreeNodeProps[];

  targetPath?: string;
  icon?: {
    icon: GenericIconName;
    color: {hex: string};
  };
}

interface Badge {
  label: string;
  textColor?: string;
  backgroundColor?: string;
}

export const NavigationTree: React.FC<NavigationTreeProps> = ({
  nodes,
  trackOrigin,
  testId,
  useButtonLink,
  allDealItems,
  ...rest
}) => {
  return (
    <nav
      data-testid={testId || 'navigationTree'}
      {...rest}
      onClick={trackOrigin}
    >
      <ul>
        {allDealItems}
        {nodes.map((node, index) => (
          <TreeNode
            node={{
              ...node,
              icon: node.icon,
            }}
            key={index}
            level={0}
            useButtonLink={useButtonLink}
          />
        ))}
      </ul>
    </nav>
  );
};

const TreeNode: React.FC<{
  node: TreeNodeProps;
  level: number;
  showChildrenInBold?: boolean;
  useButtonLink?: boolean;
}> = ({node, level, showChildrenInBold, useButtonLink}) => {
  const testId =
    node.active || (level > 0 && !node.disabled)
      ? `linkActive-${node.id}`
      : `linkInactive-${node.id}`;

  const nodeColor = (() => {
    if (!node.textColor && !node.disabled) {
      return node.active ? COLORS.Black : COLORS.Grey0;
    }

    return node.textColor;
  })();

  const handleOverrideNavigation = useHandleNavigationWithSpecialKeys({
    to: node.targetPath,
    considerScrollTopAnchor: true,
  });

  const handleOnClick = (event: React.MouseEvent) => {
    if (!node.targetPath) return;

    handleOverrideNavigation?.(event);
  };

  const treeNodeLinkProps = {
    to: useButtonLink && node.targetPath ? node.targetPath : node.path,
    $active: node.active,
    $current: node.current,
    $level: level,
    $showChildrenInBold: showChildrenInBold,
    $backgroundColor: node.current ? COLORS.Grey4 : undefined,
    $disabled: node.disabled,
  };

  return (
    <>
      <ListItem>
        {useButtonLink ? (
          <StyledLinkButton
            dataTestId={testId}
            considerScrollTopAnchor
            aria-label={undefined}
            {...treeNodeLinkProps}
          >
            <TreeNodeLinkChildren node={node} nodeColor={nodeColor} />
          </StyledLinkButton>
        ) : (
          <StyledLink
            data-testid={testId}
            component={node.disabled ? DisabledLink : undefined}
            considerScrollTopAnchor
            onClick={handleOnClick}
            aria-label={undefined}
            {...treeNodeLinkProps}
          >
            <TreeNodeLinkChildren node={node} nodeColor={nodeColor} />
          </StyledLink>
        )}
      </ListItem>

      {node.active && node.children?.length ? (
        <ListItem>
          <ChildrenWrapper data-testid={`childTree-${node.id}`} $level={level}>
            {node.children.map((childNode, index) => (
              <TreeNode
                key={index}
                node={{
                  ...childNode,
                  textColor: childNode.disabled ? undefined : COLORS.Black,
                }}
                level={level + 1}
                showChildrenInBold={node.current}
                useButtonLink={useButtonLink}
              />
            ))}
          </ChildrenWrapper>
        </ListItem>
      ) : null}
    </>
  );
};

const TreeNodeLinkChildren: React.FC<{
  node: TreeNodeProps;
  nodeColor?: string;
}> = ({node, nodeColor}) => {
  return (
    <>
      <StyledText $textColor={nodeColor} $disabled={node.disabled}>
        {node.label}
      </StyledText>
      {node.icon && (
        <StyledSaleIcon
          fill={node.icon.color.hex}
          size="XS"
          asset={node.icon.icon as IconName}
          data-testid={'saleIcon'}
        />
      )}
      {node.badges?.length ? (
        <StyledBadgeList
          entries={node.badges.map(badge => {
            return {
              kind: 'GENERIC',
              untranslatedLabel: '',
              ...badge,
            };
          })}
          disableRoundedCorners={false}
          testId={`badgeList-${node.id}`}
        />
      ) : null}
    </>
  );
};

// props contains "navigate" void function, since button does not contain that prop it throws an error, therefore it is removed from props
const DisabledLink: React.FC<
  ButtonHTMLAttributes<HTMLButtonElement> & {
    href: string;
    onClick: VoidFunction;
    navigate?: VoidFunction;
  }
> = ({href, navigate, onClick, ...props}) => {
  const history = useHistory();
  const handleOnClick = () => {
    onClick();
    history.pushWithScrollTopAnchor(href);
  };

  return <button onClick={handleOnClick} {...props} />;
};

const StyledBadgeList = styled(BadgeList)`
  display: flex;
  align-items: center;
  max-width: 40%;
`;

const StyledSaleIcon = styled(Icon)`
  margin-left: 4px;
`;

const ListItem = styled.li`
  width: 100%;
`;
